// The recorder's sound display, synthwave style: a night sky, a striped sun
// sitting on the horizon, the voice spectrum as neon bars (low voices in the
// middle, high ones outwards), mirrored on a grid floor that runs towards you
// while recording. Drawn on a canvas straight from the engine's analyser, so
// React does not re-render 30 times a second.

export const NEON = {
  night: '#0b0420',
  dusk: '#2a0b52',
  horizon: '#5a0f6e',
  floorTop: '#16052f',
  floorBottom: '#060112',
  pink: '#ff2a6d',
  magenta: '#d300c5',
  cyan: '#2de2e6',
  ice: '#c6fdff',
  yellow: '#ffd319',
};

/** Bar edges in FFT bins: the voice range, 90 Hz–5 kHz, log-spaced. */
export function voiceBands(an: AnalyserNode, bars: number): number[] {
  const hzPerBin = an.context.sampleRate / an.fftSize;
  const edges: number[] = [];
  for (let i = 0; i <= bars; i++) {
    const bin = Math.round((90 * Math.pow(5000 / 90, i / bars)) / hzPerBin);
    edges.push(Math.max(bin, i ? edges[i - 1] + 1 : 1));
  }
  return edges;
}

/** Loudest bin per band, 0–1, into `out`; returns the loudest of all. */
export function readBands(an: AnalyserNode, bins: Uint8Array<ArrayBuffer>, edges: number[], out: Float32Array): number {
  an.getByteFrequencyData(bins);
  let loudest = 0;
  for (let i = 0; i < out.length; i++) {
    let m = 0;
    for (let b = edges[i]; b < edges[i + 1] && b < bins.length; b++) m = Math.max(m, bins[b]);
    out[i] = m / 255;
    loudest = Math.max(loudest, out[i]);
  }
  return loudest;
}

/** Bars that rise at once and fall gently, with peak caps that hang a moment. */
export class Meter {
  levels: Float32Array;
  peaks: Float32Array;
  private peakAge: Float32Array;
  energy = 0;
  constructor(n: number) {
    this.levels = new Float32Array(n);
    this.peaks = new Float32Array(n);
    this.peakAge = new Float32Array(n);
  }
  /** `raw` null: nothing comes in (paused, stopped), everything settles. */
  step(raw: Float32Array | null, dt: number) {
    let sum = 0;
    for (let i = 0; i < this.levels.length; i++) {
      const v = raw ? raw[i] : 0;
      this.levels[i] = Math.max(v, this.levels[i] * Math.pow(0.82, dt / 33));
      if (this.levels[i] >= this.peaks[i]) { this.peaks[i] = this.levels[i]; this.peakAge[i] = 0; }
      else {
        this.peakAge[i] += dt;
        if (this.peakAge[i] > 350) this.peaks[i] = Math.max(this.levels[i], this.peaks[i] - dt / 1400);
      }
      sum += this.levels[i];
    }
    const mean = sum / this.levels.length;
    this.energy += (mean - this.energy) * Math.min(1, dt / 160);
  }
}

export type ScreenMode = 'live' | 'paused' | 'idle';

// A fixed sky: the same stars every frame.
const STARS = Array.from({ length: 34 }, (_, i) => {
  const r = (n: number) => { const x = Math.sin(i * 127.1 + n * 311.7) * 43758.5453; return x - Math.floor(x); };
  return { x: r(1), y: r(2) * 0.8, s: r(3) > 0.85 ? 1.4 : 0.8, a: 0.25 + r(4) * 0.55, tw: r(5) * 6.28 };
});

const SUN_BREATH = 0.07;
const SUN_GLOW = 26;

function layer(w: number, h: number, dpr: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w * dpr));
  c.height = Math.max(1, Math.round(h * dpr));
  const g = c.getContext('2d')!;
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  return [c, g];
}

/**
 * Paints the screen. Everything that does not move (sky, floor, rails, the
 * sun and its glow) is painted once per size into layers and copied in each
 * frame; only the grid's cross lines, the stars' twinkle and the bars are
 * painted live, the bars as one shape so their glow is blurred once. That
 * keeps a frame cheap enough to run at the display's own rate, which is what
 * makes the grid glide instead of stepping.
 */
export class ScreenPainter {
  private w = 0;
  private h = 0;
  private dpr = 1;
  private horizon = 0;
  private ground: HTMLCanvasElement | null = null;
  private sun: HTMLCanvasElement | null = null;
  private sunBox = { w: 0, h: 0 };

  private build(w: number, h: number, dpr: number) {
    this.w = w; this.h = h; this.dpr = dpr;
    const horizon = this.horizon = Math.round(h * 0.64);
    const cx = w / 2;

    // Sky, floor and the rails running to the vanishing point.
    const [ground, g] = layer(w, h, dpr);
    const sky = g.createLinearGradient(0, 0, 0, horizon);
    sky.addColorStop(0, NEON.night);
    sky.addColorStop(0.72, NEON.dusk);
    sky.addColorStop(1, NEON.horizon);
    g.fillStyle = sky;
    g.fillRect(0, 0, w, horizon);
    const floor = g.createLinearGradient(0, horizon, 0, h);
    floor.addColorStop(0, NEON.floorTop);
    floor.addColorStop(1, NEON.floorBottom);
    g.fillStyle = floor;
    g.fillRect(0, horizon, w, h - horizon);
    g.save();
    g.beginPath();
    g.rect(0, horizon, w, h - horizon);
    g.clip();
    g.lineWidth = 1;
    g.strokeStyle = 'rgba(255,42,109,0.5)';
    g.beginPath();
    for (let k = -14; k <= 14; k++) {
      g.moveTo(cx + k * w * 0.035, horizon);
      g.lineTo(cx + k * w * 0.2, h);
    }
    g.stroke();
    g.restore();
    this.ground = ground;

    // The sun at its largest, striped towards the bottom, with its glow;
    // each frame copies it scaled down a little when the room is quiet.
    const r = Math.min(h * 0.5, w * 0.19) * (1 + SUN_BREATH);
    const sw = 2 * (r + SUN_GLOW);
    const sh = r + SUN_GLOW;
    const [sun, s] = layer(sw, sh, dpr);
    const scx = sw / 2;
    const base = sh; // the horizon is the sprite's bottom edge
    s.save();
    s.beginPath();
    let y = 0;
    for (let k = 0; k < 6; k++) {
      const stripeAt = base - r * (0.56 - k * 0.1);
      const gap = 1.1 + k * 1.05;
      s.rect(0, y, sw, stripeAt - y);
      y = stripeAt + gap;
    }
    s.clip();
    const fill = s.createLinearGradient(0, base - r, 0, base);
    fill.addColorStop(0, NEON.yellow);
    fill.addColorStop(0.55, '#ff7b39');
    fill.addColorStop(1, NEON.pink);
    s.shadowColor = 'rgba(255,42,109,0.75)';
    s.shadowBlur = SUN_GLOW;
    s.fillStyle = fill;
    s.beginPath();
    s.arc(scx, base, r, Math.PI, 0);
    s.fill();
    s.restore();
    this.sun = sun;
    this.sunBox = { w: sw, h: sh };
  }

  /**
   * One frame. `time` in seconds drives the stars; `scroll` is how far the
   * grid has travelled (the caller advances it; fractional, never rounded).
   */
  paint(g: CanvasRenderingContext2D, w: number, h: number, dpr: number, meter: Meter, mode: ScreenMode, time: number, scroll: number) {
    if (w !== this.w || h !== this.h || dpr !== this.dpr || !this.ground) this.build(w, h, dpr);
    const horizon = this.horizon;
    const cx = w / 2;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.drawImage(this.ground!, 0, 0, w, h);

    for (const st of STARS) {
      const tw = mode === 'live' ? 0.75 + 0.25 * Math.sin(time * 1.7 + st.tw) : 1;
      g.fillStyle = `rgba(255,255,255,${(st.a * tw).toFixed(3)})`;
      g.fillRect(st.x * w, st.y * horizon * 0.8, st.s, st.s);
    }

    const k = (1 + SUN_BREATH * meter.energy) / (1 + SUN_BREATH);
    const sw = this.sunBox.w * k;
    const sh = this.sunBox.h * k;
    g.drawImage(this.sun!, cx - sw / 2, horizon - sh, sw, sh);

    // Cross lines running towards you, closer together near the horizon.
    const rows = 7;
    const off = ((scroll % 1) + 1) % 1;
    g.lineWidth = 1;
    for (let j = 0; j < rows; j++) {
      const z = (j + off) / rows;
      const ly = horizon + (h - horizon) * z * z;
      g.strokeStyle = `rgba(255,42,109,${(0.15 + 0.6 * z).toFixed(3)})`;
      g.beginPath();
      g.moveTo(0, ly);
      g.lineTo(w, ly);
      g.stroke();
    }

    // Bars, mirrored on both sides of the middle: one shape, one glow.
    const n = meter.levels.length;
    const pad = Math.max(8, w * 0.04);
    const gap = 2;
    const bw = Math.max(1.5, (w - pad * 2 - gap * (2 * n - 1)) / (2 * n));
    const maxH = horizon - 10;
    const xOf = (slot: number) => pad + slot * (bw + gap);
    g.beginPath();
    for (let i = 0; i < n; i++) {
      const bh = Math.max(1, meter.levels[i] * maxH);
      g.rect(xOf(n - 1 - i), horizon - bh, bw, bh);
      g.rect(xOf(n + i), horizon - bh, bw, bh);
    }
    const barFill = g.createLinearGradient(0, horizon, 0, horizon - maxH);
    barFill.addColorStop(0, NEON.pink);
    barFill.addColorStop(0.5, NEON.magenta);
    barFill.addColorStop(1, NEON.cyan);
    g.save();
    g.shadowColor = 'rgba(255,42,109,0.85)';
    g.shadowBlur = 10;
    g.fillStyle = barFill;
    g.fill();
    g.restore();

    g.beginPath();
    for (let i = 0; i < n; i++) {
      const bh = meter.levels[i] * (h - horizon) * 0.85;
      if (bh < 1) continue;
      g.rect(xOf(n - 1 - i), horizon + 2, bw, bh);
      g.rect(xOf(n + i), horizon + 2, bw, bh);
    }
    const mirror = g.createLinearGradient(0, horizon, 0, h);
    mirror.addColorStop(0, 'rgba(255,42,109,0.34)');
    mirror.addColorStop(1, 'rgba(255,42,109,0)');
    g.fillStyle = mirror;
    g.fill();

    g.beginPath();
    for (let i = 0; i < n; i++) {
      if (meter.peaks[i] < 0.04) continue;
      const py = horizon - meter.peaks[i] * maxH - 3;
      g.rect(xOf(n - 1 - i), py, bw, 1.5);
      g.rect(xOf(n + i), py, bw, 1.5);
    }
    g.fillStyle = NEON.ice;
    g.fill();

    // The horizon: a soft band and a bright line.
    g.fillStyle = 'rgba(255,42,109,0.35)';
    g.fillRect(0, horizon - 2, w, 4);
    g.fillStyle = '#ff6fa0';
    g.fillRect(0, horizon - 0.5, w, 1.5);

    if (mode !== 'live') {
      g.fillStyle = mode === 'paused' ? 'rgba(11,4,32,0.5)' : 'rgba(11,4,32,0.18)';
      g.fillRect(0, 0, w, h);
    }
  }
}

/** The tab's small meter: a few neon bars on a transparent ground. */
export function drawMini(g: CanvasRenderingContext2D, w: number, h: number, meter: Meter, dim: boolean) {
  g.clearRect(0, 0, w, h);
  const n = meter.levels.length;
  const gap = 2;
  const bw = (w - gap * (n - 1)) / n;
  const fill = g.createLinearGradient(0, h, 0, 0);
  fill.addColorStop(0, NEON.pink);
  fill.addColorStop(0.6, NEON.magenta);
  fill.addColorStop(1, NEON.cyan);
  g.globalAlpha = dim ? 0.45 : 1;
  g.fillStyle = fill;
  for (let i = 0; i < n; i++) {
    const bh = Math.max(2, meter.levels[i] * h);
    g.fillRect(i * (bw + gap), h - bh, bw, bh);
  }
  g.globalAlpha = 1;
}
